'use client';

import { useEffect } from 'react';

import posthog from 'posthog-js';

import { istEigenesMessrauschen } from '../posthog-eigenrauschen';
import { sanitizeEventUrls } from '../posthog-privacy';

/**
 * Phase 0.1 – cookieless, always-on PostHog Exception-only instance.
 *
 * Separate named posthog-js instance (`exception_tracker`) that captures ONLY
 * exceptions and runs OUTSIDE the CookieYes `analytics` consent gate, so errors
 * from cookie-decliners on prod are still captured (the gap vs. the self-built
 * reporter). No cookie / no device storage (`persistence: 'memory'` +
 * `cookieless_mode: 'always'`) → TTDSG §25 "strictly necessary" argument.
 *
 * 🔴 FOLGE FUER JEDE AUSWERTUNG, die Fehlerzahlen neben Trichterzahlen legt:
 * DIESE INSTANZ UND DIE ANALYTIK MESSEN VERSCHIEDENE BEVOELKERUNGEN.
 *
 * Sie laeuft ohne Einwilligung und sieht deshalb auch die Verweigerer. Die
 * Haupt-Instanz laeuft erst nach analytics-Consent — von einem Verweigerer gibt
 * es also `$exception`, aber KEINEN `$pageview`, kein `$web_vitals`, kein
 * `$pageleave`. Er existiert im Trichter nicht.
 *
 * Gemessen am 30.08.2026: von 20 Personen mit React #418 auf den bezahlten
 * Landingpages hatte KEINE EINZIGE einen Seitenaufruf im selben Zeitraum. Die
 * naheliegende Lesart — „alle 20 sind abgesprungen" — waere damit falsch: wir
 * sehen von ihnen ueberhaupt nichts ausser der Ausnahme. Weder Absprung noch
 * Erfolg ist daraus ableitbar.
 *
 * Genau diese Verwechslung hat in #1086 schon einmal in die falsche Richtung
 * gefuehrt. Wer Fehler und Trichter vergleicht, muss die Gruppen vorher
 * gleichmachen — oder es bleiben lassen.
 *
 * DEFAULT-OFF: only initialised when `NEXT_PUBLIC_POSTHOG_EXCEPTION_TRACKER`
 * is `'true'`. Prod activation is gated on (a) the PostHog project setting
 * "Cookieless Mode" being ON and (b) the legal/TTDSG sign-off. Until then this
 * is fully inert.
 *
 * NOTE for activation: when this is turned on, also flip the MAIN posthog
 * instance to `capture_exceptions: false` (in posthog-analytics-service.ts) so
 * exceptions are not double-captured when analytics consent is given.
 */
const ENABLED = process.env.NEXT_PUBLIC_POSTHOG_EXCEPTION_TRACKER === 'true';
const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '';

export function ExceptionTrackerProvider() {
  useEffect(() => {
    if (!ENABLED || !KEY || typeof window === 'undefined') return;

    posthog.init(
      KEY,
      {
        api_host: '/ingest',
        // memory-Persistence = kein Cookie, kein Device-Storage → das TTDSG-
        // §25-Argument hängt HIERAN, nicht an cookieless_mode.
        //
        // BEWUSST KEIN `cookieless_mode: 'always'`: posthog-js behandelt die
        // Instanz damit als dauerhaft opted-out (isOptedOut() === true, setzt
        // das serverseitige "Cookieless server hash mode"-Beta voraus) → es
        // wurde NIE ein Event gesendet. Live-verifiziert 2026-06-10: zwei
        // Test-Exceptions, null /ingest-Requests. Mit memory-only sendet die
        // Instanz normal; distinct_id ist eine flüchtige Random-UUID pro
        // Pageload, person_profiles 'never' → weiterhin anonym.
        persistence: 'memory',
        // #620: posthog-js injiziert nachgeladene Scripts (exception-autocapture,
        // Remote-Config) per Default VOR das erste <script> im <body> – auf
        // langsamen Geräten läuft dieser Insert mitten in Reacts Hydration und
        // erzeugt React #418 + kompletten Client-Re-Render (traf ~60% der
        // mobilen LP-Besucher; browser-verifiziert via CPU-Throttling).
        // 'head' ist hydration-safe (React 19 toleriert Head-Mutationen) und
        // ab posthog-js-Defaults '2026-01-30' aus genau diesem Grund Standard.
        external_scripts_inject_target: 'head',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_exceptions: true,
        disable_session_recording: true,
        person_profiles: 'never',
        // Finding A (Spec 2026-07-10): Remote-Config kann auf dieser Instanz
        // Captures aktivieren, die hier NIE hingehören ($web_vitals via
        // capture_performance, Surveys). Explizit aus → weniger totes Verhalten
        // + Doku. Die harte Garantie ist die before_send-Allowlist unten.
        capture_performance: false,
        disable_surveys: true,
        // Harte Event-Typ-Allowlist: diese Instanz existiert AUSSCHLIESSLICH für
        // Exceptions. Remote-Config-Flächen (Web-Vitals, Surveys, Web-Experiments,
        // Autocapture-Reaktivierung) sind Scope-Drift-Vektoren – dies ist die
        // deterministische, versions-/remote-config-feste letzte Instanz VOR dem
        // Netzwerk (Finding A). Durchgelassene $exceptions weiter URL-maskieren:
        // ein $exception auf einer Bewerber-/Consent-Seite darf keine Tokens/IDs
        // in $current_url tragen (#493).
        before_send: (event) => {
          if (event?.event !== '$exception') return null;

          // Beide Instanzen muessen denselben Filter fahren, sonst haengt es vom
          // Zufall ab, welche gerade faengt: in Prod ist
          // NEXT_PUBLIC_POSTHOG_EXCEPTION_TRACKER gesetzt, die Hauptinstanz hat
          // `capture_exceptions` also aus — dann faengt AUSSCHLIESSLICH diese
          // hier. Ein Filter nur drueben waere in Produktion wirkungslos.
          if (istEigenesMessrauschen(event)) return null;

          return sanitizeEventUrls(event);
        },
        // Independent named instance – does not touch the main posthog
        // analytics instance / its consent state.
      } as Parameters<typeof posthog.init>[1],
      'exception_tracker',
    );
  }, []);

  return null;
}
